import { useMemo, useState } from "react";
import { useCustom } from "@refinedev/core";

/**
 * De vestigingkeuze die boven de catalogus- en tariefschermen staat.
 *
 * <p>Vervangt de oude "Scope"-keuzelijst met drie vaste antwoorden (Beide / Hele organisatie /
 * Alleen deze vestiging). Die zei iets over het sóórt regel, maar niet wélke vestiging je bedoelt —
 * en met negentien vestigingen is dat juist de vraag.
 *
 * <p>De rijen dragen hun vestiging al (`vestigingId`), dus het filteren gebeurt hier in de browser,
 * net als de scope-filter die het verving. Niets aangevinkt betekent alles.
 */
/** Wat de keuze teruggeeft; schermen die hem doorgeven aan een tabblad noemen dit type. */
export interface VestigingKeuze {
  opties: string[];
  gekozen: string[];
  setGekozen: (volgende: string[]) => void;
  hoortErbij: (rij: { vestigingId?: number | null }) => boolean;
  naamVan: (id: number | null | undefined) => string | null;
  /**
   * De aangevinkte vestigingen als ids.
   *
   * <p>Voor instellingen die op één vestiging staan en niet op een rij — het prijsmodel bijvoorbeeld.
   * Filteren gaat via {@link hoortErbij}; dit is er voor de vraag "welke vestiging bedoel je", en die
   * heeft alleen een antwoord als er er precies één is aangevinkt.
   */
  gekozenIds: Set<number>;
}

export function useVestigingKeuze(): VestigingKeuze {
  const [gekozen, setGekozen] = useState<string[]>([]);
  const { data } = useCustom<{ id: number; naam: string }[]>({ url: "vestigingen", method: "get" });

  const lijst = useMemo(
    () => (data?.data ?? [])
      .filter((v) => !!v.naam)
      .sort((a, b) => a.naam.localeCompare(b.naam, "nl")),
    [data],
  );

  const gekozenIds = useMemo(
    () => new Set(lijst.filter((v) => gekozen.includes(v.naam)).map((v) => v.id)),
    [lijst, gekozen],
  );

  /** Of een rij bij de keuze hoort. Niets gekozen = alles; een rij zonder vestiging is organisatiebreed. */
  const hoortErbij = (rij: { vestigingId?: number | null }) =>
    gekozenIds.size === 0 || (rij.vestigingId != null && gekozenIds.has(rij.vestigingId));

  /** De naam bij een vestiging-id, voor schermen die alleen het id in hun rij hebben staan. */
  const naamVan = (id: number | null | undefined) =>
    (id != null ? lijst.find((v) => v.id === id)?.naam : undefined) ?? null;

  return { opties: lijst.map((v) => v.naam), gekozen, setGekozen, hoortErbij, naamVan, gekozenIds };
}
